import React, { useEffect, useReducer } from 'react';
import {
  IconButton, Input, InputGroup, Stack, Table,
} from 'rsuite';
import CloseIcon from '@rsuite/icons/Close';
import { BsPlusCircle } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { EshopCarrier, MpiDeliveryTypeLabels } from '../../../api/apiTypes';
import { getEshopCarriers } from '../../../api/carrier';
import { ApiGroup } from '../../../api/model/ApiGroup';
import { PaginationScreenKey } from '../../../services/paginationLimitService';
import useUrlPagination from '../../../hooks/urlPaginationHook';
import Pagination from '../../Pagination';
import { utils } from '../../../utils/utils';
import { CARRIER_SETUP_EDIT } from '../../../routes/links';

const FILTER_KEYS: (keyof EshopCarrier)[] = [
  'eshopCode',
  'eshopCarrierCode',
  'eshopCarrierType',
  'name',
  'identifier',
  'portalName',
];

interface State {
  eshopCarriers: EshopCarrier[],
  status: 'idle' | 'loading'
}

const initialState: State = {
  eshopCarriers: [],
  status: 'idle',
};

type CarriersLoadAction = { type: 'carriers/load' };
type CarriersSetAction = { type: 'carriers/set', payload: EshopCarrier[] };
type Action = CarriersLoadAction | CarriersSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'carriers/load':
      return { ...state, status: 'loading' };
    case 'carriers/set':
      return { ...state, status: 'idle', eshopCarriers: action.payload };
    default:
      return state;
  }
}

export default function CarrierSetupAlzaTable(): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [pagination, setPagination] = useUrlPagination(PaginationScreenKey.CARRIER_SETUP_ALZA);
  const navigate = useNavigate();

  const filteredEshopCarriers = utils.fulltextFiltered(
    state.eshopCarriers,
    pagination.filter,
    FILTER_KEYS,
  );
  const paginatedEshopCarriers = utils.paginated(
    filteredEshopCarriers,
    pagination,
  );

  function fetchEshopCarriers(signal: AbortSignal): void {
    dispatch({ type: 'carriers/load' });
    getEshopCarriers({ apiGroup: ApiGroup.ALZA }, signal)
      .then((response) => dispatch({ type: 'carriers/set', payload: response }));
  }

  useEffect(() => {
    const controller = new AbortController();
    fetchEshopCarriers(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  function handleEdit(id: number) {
    navigate(`${CARRIER_SETUP_EDIT}/${id}`);
  }

  function handleFilterChange(filter: string): void {
    setPagination({ filter });
  }

  function handleChangePage(page: number): void {
    setPagination({ page });
  }

  function handleChangeLimit(limit: number): void {
    setPagination({ limit });
  }

  function handleAddNew() {
    navigate(CARRIER_SETUP_EDIT);
  }

  return (
    <Stack
      spacing={15}
      direction="column"
      alignItems="stretch"
      justifyContent="flex-start"
    >
      <InputGroup style={{ width: 400 }}>
        <Input
          placeholder="search by marketplace, delivery method name, etc..."
          value={pagination.filter}
          onChange={(value) => handleFilterChange(value)}
        />
        <InputGroup.Button onClick={() => handleFilterChange('')}>
          <CloseIcon />
        </InputGroup.Button>
      </InputGroup>
      <Table
        autoHeight
        bordered
        data={paginatedEshopCarriers}
        loading={state.status === 'loading'}
        onRowClick={(item) => handleEdit(item.id)}
      >
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Marketplace</Table.HeaderCell>
          <Table.Cell dataKey="eshopCode" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Carrier code</Table.HeaderCell>
          <Table.Cell dataKey="eshopCarrierCode" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Carrier delivery type</Table.HeaderCell>
          <Table.Cell dataKey="eshopCarrierType" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Delivery name</Table.HeaderCell>
          <Table.Cell dataKey="name" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>MPI delivery type</Table.HeaderCell>
          <Table.Cell>
            {
              (eshopCarrier: EshopCarrier) => (eshopCarrier.defaultMpiDeliveryType
                ? MpiDeliveryTypeLabels[eshopCarrier.defaultMpiDeliveryType]
                : null)
            }
          </Table.Cell>
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>MPI carrier</Table.HeaderCell>
          <Table.Cell dataKey="identifier" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Portal name</Table.HeaderCell>
          <Table.Cell dataKey="portalName" />
        </Table.Column>
      </Table>
      <div className="mt-10">
        <Pagination
          total={filteredEshopCarriers.length}
          limit={pagination.limit}
          activePage={pagination.page}
          onChangePage={(value) => handleChangePage(value)}
          onChangeLimit={(value) => handleChangeLimit(value)}
        />
      </div>
      <IconButton
        size="md"
        appearance="subtle"
        icon={<BsPlusCircle size={20} />}
        onClick={() => handleAddNew()}
      />
    </Stack>
  );
}
